/** Workspace SKOS concepts (spec §7.5): local vocabulary for terms no ontology provides. */
import { LoroList, LoroMap, type LoroDoc } from "loro-crdt";
import { v7 as uuidv7 } from "uuid";
import { conceptIri } from "../lib/iri";

export interface Concept {
  id: string;
  iri: string;
  prefLabel: string;
  altLabels: string[];
  broader: string[];
  exactMatch: string[];
  closeMatch: string[];
  definition?: string;
}

const strings = (entry: LoroMap, key: string): string[] => {
  const v = entry.get(key);
  return v instanceof LoroList ? (v.toArray() as string[]).filter((x) => typeof x === "string") : [];
};

export function readConcepts(doc: LoroDoc, wsId: string): Concept[] {
  const map = doc.getMap("concepts");
  const out: Concept[] = [];
  for (const id of map.keys()) {
    const entry = map.get(id);
    if (!(entry instanceof LoroMap)) continue;
    out.push({
      id,
      iri: conceptIri(wsId, id),
      prefLabel: String(entry.get("prefLabel") ?? ""),
      altLabels: strings(entry, "altLabels"),
      broader: strings(entry, "broader"),
      exactMatch: strings(entry, "exactMatch"),
      closeMatch: strings(entry, "closeMatch"),
      definition: (entry.get("definition") as string | undefined) ?? undefined,
    });
  }
  return out.sort((a, b) => a.prefLabel.localeCompare(b.prefLabel));
}

export function createConcept(doc: LoroDoc, prefLabel: string, exactMatch: string[] = []): string {
  const id = uuidv7();
  const entry = doc.getMap("concepts").setContainer(id, new LoroMap());
  entry.set("prefLabel", prefLabel);
  entry.setContainer("altLabels", new LoroList());
  entry.setContainer("broader", new LoroList());
  const exact = entry.setContainer("exactMatch", new LoroList());
  exactMatch.forEach((m, i) => exact.insert(i, m));
  entry.setContainer("closeMatch", new LoroList());
  doc.commit();
  return id;
}

export function updateConcept(doc: LoroDoc, id: string, patch: Partial<Pick<Concept, "prefLabel" | "definition">>): void {
  const entry = doc.getMap("concepts").get(id);
  if (!(entry instanceof LoroMap)) return;
  if (patch.prefLabel !== undefined) entry.set("prefLabel", patch.prefLabel);
  if (patch.definition !== undefined) entry.set("definition", patch.definition);
  doc.commit();
}

export function addAltLabel(doc: LoroDoc, id: string, label: string): void {
  const entry = doc.getMap("concepts").get(id);
  if (!(entry instanceof LoroMap)) return;
  const list = entry.get("altLabels");
  const alt = list instanceof LoroList ? list : entry.setContainer("altLabels", new LoroList());
  if ((alt.toArray() as string[]).includes(label)) return;
  alt.insert(alt.length, label);
  doc.commit();
}

export function deleteConcept(doc: LoroDoc, id: string): void {
  doc.getMap("concepts").delete(id);
  doc.commit();
}
